import { CommonHttp } from "@/models/interfaces/http/common.http.request";
import {
  ExpandedFolderTree,
  FileInfoContainer,
  FolderInfoContainer,
  FolderOut,
} from "@/models/interfaces/file/file-model";
import axios from "axios";
import { Tag } from "@/store/clients";

export class FileApi {
  private static uploadController: AbortController | null = null;

  static projectFileSearch(
    projectId: number,
    searchText?: string | null,
    tagId?: number[],
  ): Promise<FolderOut> {
    const http = new CommonHttp();
    return http
      .get(
        `Files/search/project/${projectId}/?text=${searchText ? searchText : ""}&tagIds=${
          tagId ? tagId : ""
        }`,
      )
      .then((data) => data.data);
  }

  static getProjectExpansionForFolderTree(id: number, type: string, constextEntityId: number): Promise<ExpandedFolderTree> {
    const http = new CommonHttp();
    return http.get(`Files/file/${id}/catalog/?contextType=${type}&contextId=${constextEntityId}`).then((data) => data.data);
  }

  static clientFileSearch(
    clientId: number,
    searchText?: string | null,
    tagId?: number[],
  ): Promise<FolderOut> {
    const http = new CommonHttp();
    return http
      .get(
        `Files/search/client/${clientId}/?text=${searchText ? searchText : ""}&tagIds=${
          tagId ? tagId : ""
        }`,
      )
      .then((data) => data.data);
  }

  static async FileDownloadDesc(blob: Blob, name: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  static FileAndFolderDownload(
    folderId?: number[],
    fileId?: number[],
    id?: number,
    type?: string,
  ): void {
    const http = new CommonHttp();
    http
      .get(
        `Files/Download/DirList/?dirIds=${folderId ? folderId : ""}&fileIds=${
          fileId ? fileId : ""
        }`,
        { responseType: "blob" },
      )
      .then((data) =>
        FileApi.FileDownloadDesc(
          data.data,
          getFilenameFromHeaders(data.headers, folderId, id, type, true),
        ),
      );
  }

  static FileDownload(fileId: number): Promise<boolean> {
    const http = new CommonHttp();
    return http
      .get(`Files/Download/${fileId}`, { responseType: "blob" })
      .then(async (data) => {
        if (data.status !== 200) return false;
        await FileApi.FileDownloadDesc(
          data.data,
          getFilenameFromHeaders(data.headers),
        );
        return true;
      });
  }

  static FolderDownload(folderId: number): void {
    const http = new CommonHttp();
    http
      .get(`Files/Download/Dir/${folderId}`, { responseType: "blob" })
      .then((data) =>
        FileApi.FileDownloadDesc(
          data.data,
          getFilenameFromHeaders(data.headers),
        ),
      );
  }

  static async FileUpload(
    File: FormData,
    onProgress?: (percent: number) => void,
  ): Promise<any | null> {
    const http = new CommonHttp();

    // если надо отменять предыдущую загрузку при старте новой
    this.uploadController?.abort();

    const currentController = new AbortController();
    this.uploadController = currentController;

    try {
      const data = await http.post(`Files/UploadFile`, File, {
        signal: currentController.signal,

        onUploadProgress(progressEvent: any) {
          if (progressEvent.total) {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            onProgress?.(percent);
          } else if (typeof progressEvent.progress === "number") {
            const percent = Math.round(progressEvent.progress * 100);
            onProgress?.(percent);
          }
        },
      });

      return data.status === 200 ? data.data : null;
    } catch (e: any) {
      if (e.code === "ERR_CANCELED" || axios.isCancel?.(e)) {
        return null;
      }

      throw e;
    } finally {
      if (this.uploadController === currentController) {
        this.uploadController = null;
      }
    }
  }

  static AbortUpload(): void {
    this.uploadController?.abort();
    this.uploadController = null;
  }

  static PhotoUpload(photo: FormData): Promise<string> {
    const http = new CommonHttp();
    return http
      .post(`Files/UploadPhoto`, photo, {
        "Content-Type": "multipart/form-data",
      })
      .then((data) => (data.status == 200 ? data.data : null))
      .catch((error) => error);
  }

  static replaceFile(form: FormData): Promise<any> {
    const http = new CommonHttp();
    return http
      .post(`Files/replace/file/`, form, {
        "Content-Type": "multipart/form-data",
      })
      .then((data) => data.data);
  }

  static GetFileInfo(fileId: number): Promise<FileInfoContainer> {
    const http = new CommonHttp();
    return (
      http
        .get(`Files/GetFileInfo/${fileId}`)
        //@ts-ignore
        .then((data) => (data.status == 200 ? data.data : null))
    );
  }

  static GetFolderInfo(folderId: number): Promise<FolderInfoContainer> {
    const http = new CommonHttp();
    return http
      .get(`Files/GetFolderInfo/${folderId}`)
      .then((data) => (data.status == 200 ? data.data : null));
  }

  static EditFolderAccess(
    Id: number,
    GroupsWithAccess: number[],
    ClientId: number,
    ProjectId: number,
  ): Promise<FolderOut> {
    const http = new CommonHttp();
    return http
      .post(`Files/EditFolderAccess`, {
        Id: Id,
        GroupsWithAccess: GroupsWithAccess,
        ClientId: ClientId,
        ProjectId: ProjectId,
      })
      .then((data) => data.data);
  }

  static EditFileAccess(
    Id: number,
    GroupsWithAccess: number[],
    ClientId: number,
    ProjectId: number,
  ): Promise<FolderOut> {
    const http = new CommonHttp();
    return http
      .post(`Files/EditFileAccess`, {
        Id: Id,
        GroupsWithAccess: GroupsWithAccess,
        ClientId: ClientId,
        ProjectId: ProjectId,
      })
      .then((data) => data.data);
  }

  static MoveDir(
    parentFolderId: number,
    folderId: number[],
    fileId: number[],
  ): Promise<FolderOut> {
    const http = new CommonHttp();
    return http
      .post(`Files/Move/DirList`, {
        parentId: parentFolderId,
        directoryIds: folderId,
        filesIds: fileId,
      })
      .then((data) => data.data);
  }

  static MoveFile(folderId: number[], fileId: number[]): Promise<FolderOut> {
    const http = new CommonHttp();
    return http
      .post(`Files/move/file/${fileId}/`, {
        NewFolderId: folderId,
      })
      .then((data) => data.data);
  }

  static RenameDir(Name: string, folderId: number[]): Promise<FolderOut> {
    const http = new CommonHttp();
    return http
      .post(`Files/Rename/Dir/${folderId}`, {
        Name: Name,
      })
      .then((data) => data.data);
  }

  static RenameFile(Name: string, fileId: number[]): Promise<FolderOut> {
    const http = new CommonHttp();
    return http
      .post(`Files/Rename/File/${fileId}`, {
        Name: Name,
      })
      .then((data) => data.data);
  }

  static RemoveDir(folderId: number) {
    const http = new CommonHttp();
    return http
      .post(`Files/remove/dir/${folderId}/`, {})
      .then((data) => data.data);
  }

  static RemoveFile(fileId: number) {
    const http = new CommonHttp();
    return http
      .post(`Files/remove/file/${fileId}/`, {})
      .then((data) => data.data);
  }

  static getProjectFolders(id: number): Promise<FolderOut> {
    const http = new CommonHttp();
    return http.get(`Files/project/${id}/folders/`).then((data) => data.data);
  }

  static getClientFolders(id: number): Promise<FolderOut> {
    const http = new CommonHttp();
    return http.get(`Files/client/${id}/folders/`).then((data) => data.data);
  }

  static getChildFolders(parentFolderId: number): Promise<FolderOut> {
    const http = new CommonHttp();
    return http
      .get(`Files/folders/${parentFolderId}/children/`)
      .then((data) => data.data);
  }

  static CreateNewDirectory(
    folderId: number,
    name: string,
    tags?: Tag[],
  ): Promise<FolderOut> {
    const http = new CommonHttp();
    return http
      .post(`Files/CreateDirectory`, {
        ParentDirectoryId: folderId,
        Name: name,
      })
      .then((data) => (data.status == 200 ? data.data : null));
  }
}

export function getFilenameFromContentDisposition(cd?: string): string {
  if (!cd) return "download";

  const star = cd.match(/filename\*\s*=\s*(?:utf-8|UTF-8)''([^;]+)/);
  if (star?.[1]) {
    return cleanFilename(decodeURIComponent(star[1]));
  }

  const normal = cd.match(/filename\s*=\s*"?([^";]+)"?/);
  if (normal?.[1]) {
    return cleanFilename(normal[1]);
  }

  return "download";
}

function cleanFilename(filename: string): string {
  if (!filename) return "download";

  let cleaned = filename.trim();

  cleaned = cleaned.replace(/\\(["'])/g, "$1");

  if (
    (cleaned.startsWith('"') && cleaned.endsWith('"')) ||
    (cleaned.startsWith("'") && cleaned.endsWith("'"))
  ) {
    cleaned = cleaned.slice(1, -1);
  }

  if (cleaned.includes("%")) {
    cleaned = decodeURIComponent(cleaned);
  }

  const lastSlash = Math.max(
    cleaned.lastIndexOf("/"),
    cleaned.lastIndexOf("\\"),
  );
  if (lastSlash !== -1) {
    cleaned = cleaned.substring(lastSlash + 1);
  }

  cleaned = cleaned.replace(/[/\\:*?"<>|]/g, "_");

  cleaned = cleaned.replace(/(\d{14})(?=\.zip$)/i, "");

  return cleaned;
}

export function getFilenameFromHeaders(
  headers: any,
  folderId?: number[],
  id?: number,
  type?: string,
  selectedElements?: boolean,
) {
  if (!headers) return "archive.zip";

  let contentDisposition;

  if (headers["content-disposition"]) {
    contentDisposition = headers["content-disposition"];
  } else if (headers["Content-Disposition"]) {
    contentDisposition = headers["Content-Disposition"];
  }

  if (selectedElements) {
    return `${type}${id}.zip`;
  }

  return getFilenameFromContentDisposition(contentDisposition);
}
